import React, { useEffect, useMemo, useState } from "react";
import { Alert, Pressable, Text, View } from "react-native";
import { ArrowDown, ArrowUp, Edit3, Plus, Save, Trash2, Upload, FileText, DollarSign, CheckSquare } from "lucide-react-native";
import { api } from "../api/client";
import { canViewProfit as checkCanViewProfit, isAdmin as checkIsAdmin, isManager as checkIsManager, isSuperAdmin as checkIsSuperAdmin } from "../utils/format";
import { ActionMenu, Badge, Button, Card, DataTable, Field, IconButton, ScreenHeader, Select, Toolbar } from "../components/UI";
import { colors, styles } from "../styles";
import { formatAmount, formatCurrency, formatPercent } from "../utils/format";

const today = () => new Date().toISOString().slice(0, 10);
const emptyItem = {
  category_id: "",
  sub_category_id: "",
  product_id: "",
  item_code: "",
  item_name: "",
  description: "",
  brand: "",
  model: "",
  unit: "Nos",
  quantity: "1",
  actual_price: "0",
  unit_price: "0",
  discount: "0",
  tax: "0",
  remarks: ""
};

export function QuotationFormScreen({ id, user, onSaved }) {
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [terms, setTerms] = useState([]);
  const [selectedTerms, setSelectedTerms] = useState([]);
  const [customTerm, setCustomTerm] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [importingItems, setImportingItems] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [approvedImportRows, setApprovedImportRows] = useState([]);
  const [loading, setLoading] = useState(Boolean(id));
  const [loadError, setLoadError] = useState("");
  const [form, setForm] = useState({
    client_id: "",
    quotation_date: today(),
    validity_date: "",
    project_title: "",
    extra_charges: "0",
    terms: "",
    remarks: "",
    status: "draft",
    items: []
  });
  const [item, setItem] = useState(emptyItem);
  const canViewProfit = checkCanViewProfit(user);
  const isAdmin = checkIsAdmin(user);
  const isManager = checkIsManager(user);
  const isSuper = checkIsSuperAdmin(user);

  useEffect(() => {
    async function load() {
      setLoading(Boolean(id));
      setLoadError("");
      console.log("[Quotation Edit] Page mounted", { id });
      console.log("[Quotation Edit] Quotation ID received", id);
      const [clientRows, categoryData, termRows] = await Promise.all([
        api.clients(),
        api.categories(),
        api.terms(true)
      ]);
      setClients(clientRows);
      setCategories(categoryData.main_categories || []);
      setSubCategories(categoryData.sub_categories || []);
      setTerms(termRows);

      if (id) {
        console.log("[Quotation Edit] Fetch quotation API called", id);
        const q = await api.quotation(id);
        console.log("[Quotation Edit] Fetch quotation API response", q);
        setForm({
          ...q,
          client_id: String(q.client_id),
          extra_charges: String(q.extra_charges || 0),
          items: (q.items || []).map(normalizeItem)
        });
        console.log("[Quotation Edit] Items loaded count", (q.items || []).length);
        setSelectedTerms((q.selected_terms || []).map((term, index) => ({
          term_id: term.term_id,
          term_text_snapshot: term.term_text_snapshot,
          display_order: term.display_order || index + 1,
          custom: !term.term_id || !termRows.some((masterTerm) => String(masterTerm.id) === String(term.term_id))
        })));
        console.log("[Quotation Edit] Terms loaded count", (q.selected_terms || []).length);
      } else {
        const next = await api.nextQuotationNumber();
        setForm((f) => ({ ...f, quotation_number: next.quotation_number }));
        setSelectedTerms(termRows.filter((term) => term.is_default).map((term) => ({
          term_id: term.id,
          term_text_snapshot: term.term_text,
          display_order: term.display_order
        })));
      }
    }
    load()
      .catch((error) => {
        console.error("[Quotation Edit] Failed to load quotation form", error);
        setLoadError(error.message || "Unable to load quotation.");
        Alert.alert("Quotation", error.message);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const showSaveMessage = (type, message) => {
    setSaveMessage({ type, message });
    if (type === "error") console.error("[Quotation Save]", message);
    else console.log("[Quotation Save]", message);
  };

  const normalizeItem = (row) => ({
    ...row,
    category_id: row.category_id ? String(row.category_id) : "",
    product_id: row.product_id ? String(row.product_id) : "",
    sub_category_id: row.sub_category_id ? String(row.sub_category_id) : "",
    item_code: row.item_code || "",
    brand: row.brand || "",
    model: row.model || "",
    short_description: row.short_description || "",
    long_description: row.long_description || "",
    source_item_name: row.source_item_name || "",
    source_description: row.source_description || "",
    import_match_status: row.import_match_status || "",
    import_match_confidence: row.import_match_confidence || "",
    quantity: String(row.quantity ?? "1"),
    actual_price: String(row.actual_price ?? "0"),
    unit_price: String(row.unit_price ?? "0"),
    discount: String(row.discount ?? "0"),
    tax: String(row.tax ?? "0")
  });

  const totals = useMemo(() => {
    const subtotal = form.items.reduce((sum, row) => sum + Number(row.quantity || 0) * Number(row.unit_price || 0), 0);
    const discount = form.items.reduce((sum, row) => sum + Number(row.discount || 0), 0);
    const tax = form.items.reduce((sum, row) => sum + Number(row.tax || 0), 0);
    return { subtotal, discount, tax, grand: subtotal - discount + tax + Number(form.extra_charges || 0) };
  }, [form.items, form.extra_charges]);

  const profitSummary = useMemo(() => {
    const totalActualCost = form.items.reduce((sum, row) => {
      return sum + Number(row.quantity || 0) * Number(row.actual_price || 0);
    }, 0);
    const missingCostCount = form.items.filter((row) => Number(row.actual_price || 0) <= 0).length;
    const profitBaseAmount = totals.subtotal - totals.discount + Number(form.extra_charges || 0);
    const expectedProfit = profitBaseAmount - totalActualCost;
    const profitPercentage = profitBaseAmount > 0 ? (expectedProfit / profitBaseAmount) * 100 : 0;
    return {
      totalQuotationAmount: profitBaseAmount,
      totalActualCost,
      expectedProfit,
      profitPercentage,
      missingCostCount
    };
  }, [form.items, totals.subtotal, totals.discount, form.extra_charges]);

  const loadProducts = async (categoryId = item.category_id, subCategoryId = item.sub_category_id, search = productSearch) => {
    if (!categoryId) {
      setProducts([]);
      return;
    }
    try {
      setProducts(await api.products(search, categoryId, true, subCategoryId));
    } catch (error) {
      Alert.alert("Products", error.message);
    }
  };

  const chooseCategory = async (categoryId) => {
    setEditingItemIndex(null);
    const next = { ...emptyItem, category_id: categoryId, sub_category_id: "" };
    setItem(next);
    await loadProducts(categoryId, "", productSearch);
  };

  const chooseSubCategory = async (subCategoryId) => {
    const next = { ...item, sub_category_id: subCategoryId, product_id: "" };
    setItem(next);
    await loadProducts(item.category_id, subCategoryId, productSearch);
  };

  const searchProducts = async (search) => {
    setProductSearch(search);
    await loadProducts(item.category_id, item.sub_category_id, search);
  };

  const chooseProduct = (productId) => {
    const product = products.find((p) => String(p.id) === String(productId));
    if (!product) return;
    setItem({
      category_id: String(product.main_category_id || item.category_id || ""),
      sub_category_id: String(product.sub_category_id || item.sub_category_id || ""),
      product_id: product.id,
      item_code: product.item_code || "",
      item_name: product.item_name,
      description: product.long_description || product.item_description || product.short_description || "",
      short_description: product.short_description || product.item_description || "",
      long_description: product.long_description || product.item_description || product.short_description || "",
      brand: product.brand || "",
      model: product.model || "",
      unit: product.unit || "Nos",
      quantity: "1",
      actual_price: String(product.actual_price || 0),
      unit_price: String(product.unit_price || 0),
      discount: "0",
      tax: "0",
      remarks: ""
    });
  };

  const addItem = () => {
    if (!canEditStatus(form.status)) return Alert.alert("Quotation", "Submitted or approved quotations cannot be edited.");
    if (!item.item_name) return Alert.alert("Item required", "Select a product or enter a custom item name.");
    setForm((current) => {
      if (editingItemIndex == null) return { ...current, items: [...current.items, item] };
      const nextItems = [...current.items];
      nextItems[editingItemIndex] = item;
      return { ...current, items: nextItems };
    });
    setEditingItemIndex(null);
    setItem({ ...emptyItem, category_id: item.category_id, sub_category_id: item.sub_category_id });
    loadProducts(item.category_id, item.sub_category_id, productSearch);
  };

  const importQuotationItems = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!canEditStatus(form.status)) return Alert.alert("Quotation", "Submitted or approved quotations cannot be edited.");

    setImportingItems(true);
    setImportResult(null);
    setApprovedImportRows([]);
    try {
      const result = await api.importQuotationItems(file);
      setImportResult(result);
      setApprovedImportRows((result.matched || []).map((row) => String(row.row_number)));
      showSaveMessage(
        result.unmatched?.length ? "error" : "success",
        `Import parsed ${result.total_rows || 0} rows: ${result.matched?.length || 0} matched, ${result.unmatched?.length || 0} unmatched. Review before adding.`
      );
    } catch (error) {
      showSaveMessage("error", error.message || "Quotation items could not be imported.");
      Alert.alert("Import Quotation Items", error.message || "Quotation items could not be imported.");
    } finally {
      setImportingItems(false);
    }
  };

  const toggleApprovedImportRow = (rowNumber) => {
    const key = String(rowNumber);
    setApprovedImportRows((current) => (
      current.includes(key) ? current.filter((row) => row !== key) : [...current, key]
    ));
  };

  const addApprovedImportedItems = () => {
    if (!importResult?.matched?.length) return;
    const approved = importResult.matched
      .filter((row) => approvedImportRows.includes(String(row.row_number)))
      .map((row) => normalizeItem(row.item));
    if (approved.length === 0) {
      Alert.alert("Import Quotation Items", "Please approve at least one matched item.");
      return;
    }
    setForm((current) => ({ ...current, items: [...current.items, ...approved] }));
    setImportResult(null);
    setApprovedImportRows([]);
    showSaveMessage("success", `${approved.length} imported item${approved.length === 1 ? "" : "s"} added to quotation.`);
  };

  const editItem = async (index) => {
    const selected = normalizeItem(form.items[index]);
    setEditingItemIndex(index);
    setItem(selected);
    if (selected.category_id) {
      try {
        setProducts(await api.products(productSearch, selected.category_id, true, selected.sub_category_id));
      } catch (error) {
        setProducts([]);
      }
    }
  };

  const moveInsideCategory = (index, direction) => {
    const row = form.items[index];
    const sameCategory = form.items
      .map((candidate, candidateIndex) => ({ candidate, candidateIndex }))
      .filter(({ candidate }) => String(candidate.category_id || "") === String(row.category_id || ""));
    const localIndex = sameCategory.findIndex(({ candidateIndex }) => candidateIndex === index);
    const swap = sameCategory[localIndex + direction];
    if (!swap) return;
    const nextItems = [...form.items];
    [nextItems[index], nextItems[swap.candidateIndex]] = [nextItems[swap.candidateIndex], nextItems[index]];
    setForm({ ...form, items: nextItems });
  };

  const groupedItems = useMemo(() => {
    const categorized = categories
      .map((category) => ({
        category,
        rows: form.items
          .map((row, index) => ({ ...row, rowIndex: index, id: index }))
          .filter((row) => String(row.category_id || "") === String(category.id))
      }))
      .filter((group) => group.rows.length > 0);

    const activeCategoryIds = new Set(categories.map((category) => String(category.id)));
    const unmatchedRows = form.items
      .map((row, index) => ({ ...row, rowIndex: index, id: index }))
      .filter((row) => !row.category_id || !activeCategoryIds.has(String(row.category_id)));

    const inactiveGroups = unmatchedRows.reduce((groups, row) => {
      const name = row.category_name || "Other Items";
      if (!groups[name]) groups[name] = [];
      groups[name].push(row);
      return groups;
    }, {});

    return categorized.concat(
      Object.entries(inactiveGroups).map(([name, rows]) => ({ category: { id: `inactive-${name}`, name }, rows }))
    );
  }, [categories, form.items]);

  const toggleTerm = (term) => {
    const exists = selectedTerms.some((selected) => String(selected.term_id) === String(term.id));
    if (exists) {
      setSelectedTerms(selectedTerms.filter((selected) => String(selected.term_id) !== String(term.id)));
    } else {
      setSelectedTerms([...selectedTerms, {
        term_id: term.id,
        term_text_snapshot: term.term_text,
        display_order: term.display_order
      }]);
    }
  };

  const addCustomTerm = () => {
    if (!customTerm.trim()) return;
    setSelectedTerms([...selectedTerms, {
      term_id: null,
      term_text_snapshot: customTerm.trim(),
      display_order: selectedTerms.length + 1,
      custom: true
    }]);
    setCustomTerm("");
  };

  const save = async (mode = "same_version") => {
    console.log("[Quotation Save] Save button clicked", { mode });
    if (saving) return;

    setSaveMessage(null);

    if (!form.client_id) {
      showSaveMessage("error", "Please select a client before saving the quotation.");
      return;
    }
    if (!(form.project_title || "").trim()) {
      showSaveMessage("error", "Please enter the project title or subject.");
      return;
    }
    if (form.items.length === 0) {
      showSaveMessage("error", "Please add at least one quotation item.");
      return;
    }

    let revisionNotes = "";
    if (id && mode === "new_revision") {
      const nextRevNum = (Number(form.revision_number) || 0) + 1;
      const promptNotes = window.prompt ? window.prompt("Enter revision remarks / notes for this new version:", `Revision ${nextRevNum}`) : `Revision ${nextRevNum}`;
      if (promptNotes === null) return;
      revisionNotes = promptNotes;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        save_mode: mode,
        revision_notes: revisionNotes,
        client_id: Number(form.client_id),
        extra_charges: Number(form.extra_charges || 0),
        items: form.items.map((row) => ({
          ...row,
          product_id: row.product_id ? Number(row.product_id) : null,
          category_id: row.category_id ? Number(row.category_id) : null,
          sub_category_id: row.sub_category_id ? Number(row.sub_category_id) : null,
          quantity: Number(row.quantity || 0),
          unit_price: Number(row.unit_price || 0),
          discount: Number(row.discount || 0),
          tax: Number(row.tax || 0)
        })),
        selected_terms: selectedTerms.map((term, index) => ({
          term_id: term.term_id || null,
          term_text_snapshot: term.term_text_snapshot,
          display_order: term.display_order || index + 1
        }))
      };

      const saved = await api.saveQuotation(payload);
      const msg = mode === "new_revision"
        ? `New revision ${saved.quotation_number} (Rev ${saved.revision_number}) created successfully.`
        : `${saved.quotation_number} saved successfully.`;
      showSaveMessage("success", msg);
      Alert.alert("Quotation saved", msg);
      onSaved();
    } catch (error) {
      showSaveMessage("error", error.message || "Quotation save failed. Please check the entered details and try again.");
      Alert.alert("Quotation", error.message);
    } finally {
      setSaving(false);
    }
  };

  const submitForApproval = async () => {
    if (!id) {
      showSaveMessage("error", "Please save the draft before submitting for approval.");
      return;
    }
    try {
      await api.submitQuotation(id);
      showSaveMessage("success", "Quotation submitted for approval.");
      onSaved();
    } catch (error) {
      showSaveMessage("error", error.message);
    }
  };

  const isOwner = !form.created_by_user_id || Number(form.created_by_user_id) === Number(user?.id);
  const editable = isAdmin || (isManager && isOwner && canEditStatus(form.status));

  return (
    <View style={styles.page}>
      <ScreenHeader title={id ? "Edit Quotation" : "New Quotation"} subtitle={form.quotation_number || "Auto quotation number"}>
        <View style={{ flexDirection: "row", gap: 8, alignItems: "center" }}>
          {Number(form.revision_number || 0) > 0 ? (
            <Badge variant="warning">
              REV {form.revision_number}
            </Badge>
          ) : (
            <Badge variant="secondary">
              REV 0
            </Badge>
          )}
          <Badge variant={form.status === "approved" ? "success" : form.status === "rejected" ? "danger" : "pending"}>
            STATUS: {statusLabel(form.status).toUpperCase()}
          </Badge>
        </View>
      </ScreenHeader>

      {loading ? <Text style={[styles.message, styles.successMessage]}>Loading quotation details...</Text> : null}
      {loadError ? <Text style={[styles.message, styles.errorMessage]}>{loadError}</Text> : null}
      {saveMessage ? (
        <Text style={[styles.message, saveMessage.type === "error" ? styles.errorMessage : styles.successMessage]}>
          {saveMessage.message}
        </Text>
      ) : null}

      {/* Main Quotation Details */}
      <Card>
        <Text style={styles.h2}>Client & Project Details</Text>
        <View style={styles.row}>
          <Select label="Select Client *" value={form.client_id} onChangeText={(v) => setForm({ ...form, client_id: v })} style={{ flex: 2 }}>
            <option value="">Choose Client Company...</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </Select>
          <Field label="Quotation Date" value={form.quotation_date} onChangeText={(v) => setForm({ ...form, quotation_date: v })} placeholder="YYYY-MM-DD" />
          <Field label="Validity Date" value={form.validity_date} onChangeText={(v) => setForm({ ...form, validity_date: v })} placeholder="YYYY-MM-DD" />
          <Field label="Project Title / Subject *" value={form.project_title} onChangeText={(v) => setForm({ ...form, project_title: v })} placeholder="e.g. CCTV & Networking System" style={{ flex: 3 }} />
        </View>
      </Card>

      {/* Import Quotation Items CSV */}
      <Card>
        <Text style={styles.h2}>Import Quotation Items (CSV / Excel)</Text>
        <Text style={styles.small}>Upload CSV/XLSX with ProductCodes and quantities. Unit prices are automatically loaded from Product Master.</Text>
        <View style={styles.row}>
          <input
            type="file"
            accept=".csv,.xlsx,.xls"
            disabled={!editable || importingItems}
            onChange={importQuotationItems}
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: "12px",
              padding: "10px 14px",
              backgroundColor: "#fff",
              minWidth: 280,
              fontSize: "14px"
            }}
          />
          <Button icon={Upload} variant="secondary" disabled>{importingItems ? "Importing..." : "Upload & Parse CSV"}</Button>
        </View>

        {importResult ? (
          <View style={{ gap: 12, marginTop: 12 }}>
            <Badge variant="info">
              {importResult.file_name}: {importResult.matched?.length || 0} matched / {importResult.unmatched?.length || 0} unmatched
            </Badge>

            {(importResult.matched || []).length > 0 ? (
              <View style={{ gap: 12 }}>
                <Text style={styles.h3}>Review Matched Items</Text>
                <DataTable
                  columns={[
                    {
                      key: "approve",
                      label: "Approve",
                      render: (row) => {
                        const checked = approvedImportRows.includes(String(row.row_number));
                        return (
                          <Pressable onPress={() => toggleApprovedImportRow(row.row_number)} style={styles.checkRow}>
                            <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
                              {checked && <Text style={{ color: "#fff", fontSize: 12, fontWeight: "800" }}>✓</Text>}
                            </View>
                          </Pressable>
                        );
                      }
                    },
                    { key: "item_code", label: "Code", render: (r) => r.item.item_code },
                    { key: "item_name", label: "Item Name", render: (r) => r.item.item_name, style: { flex: 2 } },
                    { key: "quantity", label: "Qty", render: (r) => r.item.quantity },
                    { key: "unit_price", label: "Master Price", render: (r) => formatAmount(r.item.unit_price) },
                    { key: "line_total", label: "Line Total", render: (r) => formatAmount(r.item.line_total) }
                  ]}
                  rows={importResult.matched}
                />
                <View style={styles.row}>
                  <Button icon={Plus} onPress={addApprovedImportedItems} disabled={!editable}>Add Approved Items</Button>
                  <Button variant="secondary" onPress={() => { setImportResult(null); setApprovedImportRows([]); }}>Clear Review</Button>
                </View>
              </View>
            ) : null}
          </View>
        ) : null}
      </Card>

      {/* Add / Edit Item Form */}
      <Card>
        <Text style={styles.h2}>{editingItemIndex == null ? "Add Quotation Item" : "Edit Quotation Item"}</Text>
        <View style={styles.row}>
          <Select label="Main Category" value={item.category_id} onChangeText={chooseCategory}>
            <option value="">Select Category...</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Select label="Sub Category" value={item.sub_category_id} onChangeText={chooseSubCategory}>
            <option value="">{item.category_id ? "All Sub Categories" : "Select main category first"}</option>
            {subCategories
              .filter((sub) => String(sub.main_category_id) === String(item.category_id))
              .map((sub) => <option key={sub.id} value={sub.id}>{sub.name}</option>)}
          </Select>
          <Field label="Search Catalog" value={productSearch} onChangeText={searchProducts} placeholder="Filter catalog..." />
          <Select label="Select Product" value={item.product_id} onChangeText={chooseProduct} style={{ flex: 2 }}>
            <option value="">{item.category_id ? "Select Product from Master..." : "Select category first"}</option>
            {products.map((p) => <option key={p.id} value={p.id}>{[p.item_code, p.item_name, p.model].filter(Boolean).join(" - ")}</option>)}
          </Select>
        </View>

        <View style={styles.row}>
          <Field label="Product Code" value={item.item_code} onChangeText={(v) => setItem({ ...item, item_code: v })} />
          <Field label="Item Name *" value={item.item_name} onChangeText={(v) => setItem({ ...item, item_name: v })} style={{ flex: 2 }} />
          <Field label="Brand / Make" value={item.brand} onChangeText={(v) => setItem({ ...item, brand: v })} />
          <Field label="Model No" value={item.model} onChangeText={(v) => setItem({ ...item, model: v })} />
          <Field label="Unit" value={item.unit} onChangeText={(v) => setItem({ ...item, unit: v })} />
        </View>

        <View style={styles.row}>
          <Field label="Quantity" value={item.quantity} onChangeText={(v) => setItem({ ...item, quantity: v })} keyboardType="numeric" />
          <Field label="Unit Price (PKR)" value={item.unit_price} onChangeText={(v) => setItem({ ...item, unit_price: v })} keyboardType="numeric" />
          <Field label="Discount (PKR)" value={item.discount} onChangeText={(v) => setItem({ ...item, discount: v })} keyboardType="numeric" />
          <Field label="Tax (PKR)" value={item.tax} onChangeText={(v) => setItem({ ...item, tax: v })} keyboardType="numeric" />
        </View>

        <View style={styles.row}>
          <Field label="Item Specification / Description" value={item.description} onChangeText={(v) => setItem({ ...item, description: v })} multiline style={{ flex: 3 }} />
          <Field label="Item Remarks" value={item.remarks} onChangeText={(v) => setItem({ ...item, remarks: v })} multiline style={{ flex: 2 }} />
        </View>

        <Button icon={Plus} onPress={addItem} disabled={!editable} style={{ marginTop: 8 }}>
          {editingItemIndex == null ? "Add Item to Quotation" : "Update Item"}
        </Button>
      </Card>

      {/* Categorized Item Tables */}
      {groupedItems.map((group) => (
        <Card key={group.category.id || "other"}>
          <Text style={styles.h2}>{group.category.name}</Text>
          <DataTable
            actionsWidth={56}
            columns={[
              { key: "item_name", label: "Item Name", flex: 2, style: { fontWeight: "700" } },
              { key: "description", label: "Description", flex: 2.5 },
              { key: "unit", label: "Unit", flex: 0.8 },
              { key: "quantity", label: "Qty", flex: 0.8 },
              { key: "unit_price", label: "Unit Price", flex: 1.2, render: (r) => formatAmount(r.unit_price) },
              {
                key: "line",
                label: "Line Total",
                flex: 1.3,
                render: (r) => formatAmount(Number(r.quantity || 0) * Number(r.unit_price || 0) - Number(r.discount || 0) + Number(r.tax || 0))
              }
            ]}
            rows={group.rows}
            renderActions={(row) => (
              <ActionMenu
                actions={[
                  editable && { label: "Move Up", icon: ArrowUp, onPress: () => moveInsideCategory(row.rowIndex, -1) },
                  editable && { label: "Move Down", icon: ArrowDown, onPress: () => moveInsideCategory(row.rowIndex, 1) },
                  editable && { label: "Edit Item", icon: Edit3, onPress: () => editItem(row.rowIndex) },
                  editable && { label: "Remove Item", icon: Trash2, variant: "danger", onPress: () => setForm({ ...form, items: form.items.filter((_, i) => i !== row.rowIndex) }) }
                ]}
              />
            )}
          />
        </Card>
      ))}

      {/* Quotation Summary & Totals */}
      <Card>
        <Text style={styles.h2}>Summary & Totals</Text>
        <View style={styles.row}>
          <Field label="Extra Charges (PKR)" value={form.extra_charges} onChangeText={(v) => setForm({ ...form, extra_charges: v })} keyboardType="numeric" />
        </View>
        <View style={styles.row}>
          <Badge variant="secondary">Subtotal: {formatAmount(totals.subtotal)}</Badge>
          <Badge variant="secondary">Total Discount: {formatAmount(totals.discount)}</Badge>
          <Badge variant="secondary">Total Tax: {formatAmount(totals.tax)}</Badge>
          <Badge variant="secondary">Extra Charges: {formatAmount(form.extra_charges)}</Badge>
          <Badge variant="success">Grand Total: {formatAmount(totals.grand)}</Badge>
        </View>

        {canViewProfit ? (
          <View style={profitPanelStyle}>
            <View style={styles.between}>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                <DollarSign size={20} color={colors.primary} />
                <Text style={styles.h3}>Internal Profit Analysis</Text>
              </View>
              <Badge variant="info">Management View Only</Badge>
            </View>
            <View style={styles.row}>
              <View style={profitMetricStyle}>
                <Text style={styles.small}>Quotation Base Amount</Text>
                <Text style={profitValueStyle}>{formatCurrency(profitSummary.totalQuotationAmount)}</Text>
              </View>
              <View style={profitMetricStyle}>
                <Text style={styles.small}>Total Actual Cost</Text>
                <Text style={profitValueStyle}>{formatCurrency(profitSummary.totalActualCost)}</Text>
              </View>
              <View style={profitMetricStyle}>
                <Text style={styles.small}>Expected Net Profit</Text>
                <Text style={[profitValueStyle, { color: profitSummary.expectedProfit < 0 ? colors.danger : colors.primary }]}>
                  {formatCurrency(profitSummary.expectedProfit)}
                </Text>
              </View>
              <View style={profitMetricStyle}>
                <Text style={styles.small}>Profit Margin</Text>
                <Text style={[profitValueStyle, { color: profitSummary.profitPercentage < 0 ? colors.danger : colors.primary }]}>
                  {formatPercent(profitSummary.profitPercentage)}
                </Text>
              </View>
            </View>
            {profitSummary.missingCostCount > 0 ? (
              <Text style={[styles.message, styles.errorMessage]}>
                Warning: {profitSummary.missingCostCount} product(s) have missing Actual Cost values. Profit calculations may be incomplete.
              </Text>
            ) : null}
          </View>
        ) : null}
      </Card>

      {/* Terms & Conditions */}
      <Card>
        <Text style={styles.h2}>Terms & Conditions</Text>
        <View style={styles.termList}>
          {terms.map((term) => {
            const checked = selectedTerms.some((selected) => String(selected.term_id) === String(term.id));
            return (
              <Pressable key={term.id} onPress={() => toggleTerm(term)} style={styles.checkRow}>
                <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
                  {checked && <Text style={{ color: "#fff", fontSize: 12, fontWeight: "800" }}>✓</Text>}
                </View>
                <Text style={styles.checkLabel}>{term.term_text}</Text>
              </Pressable>
            );
          })}
          {selectedTerms.filter((term) => term.custom).map((term, index) => (
            <View key={`${term.term_text_snapshot}-${index}`} style={styles.between}>
              <Text style={styles.body}>• {term.term_text_snapshot}</Text>
              <Button icon={Trash2} variant="danger" onPress={() => setSelectedTerms(selectedTerms.filter((candidate) => candidate !== term))} style={{ height: 36, paddingHorizontal: 12 }}>Remove</Button>
            </View>
          ))}
        </View>

        <View style={styles.row}>
          <Field label="Custom Specific Term" value={customTerm} onChangeText={setCustomTerm} placeholder="Type custom term and click Add..." style={{ flex: 3 }} />
          <Button icon={Plus} variant="secondary" onPress={addCustomTerm}>Add Custom Term</Button>
        </View>

        <View style={styles.row}>
          <Field label="Internal Remarks / Notes" value={form.remarks} onChangeText={(v) => setForm({ ...form, remarks: v })} multiline />
        </View>

        <Toolbar style={{ marginTop: 12 }}>
          {id ? (
            <>
              <Button icon={Save} onPress={() => save("same_version")} disabled={saving || !editable} loading={saving}>
                Save (Current Version)
              </Button>
              <Button icon={Plus} variant="secondary" onPress={() => save("new_revision")} disabled={saving || !editable} loading={saving}>
                Save as New Revision (R+1)
              </Button>
            </>
          ) : (
            <Button icon={Save} onPress={() => save("same_version")} disabled={saving || !editable} loading={saving}>
              Save Draft Quotation
            </Button>
          )}
          <Button icon={Save} variant="secondary" onPress={submitForApproval} disabled={!editable || !id || !canSubmitStatus(form.status)}>
            Submit for Approval
          </Button>
        </Toolbar>
      </Card>

      {/* Revision History Section */}
      {id && Array.isArray(form.revisions) && form.revisions.length > 0 ? (
        <Card style={{ marginTop: 16 }}>
          <Text style={styles.h2}>Revision History ({form.revisions.length} previous {form.revisions.length === 1 ? "revision" : "revisions"})</Text>
          <Text style={styles.small}>Every revision is permanently preserved with complete items, prices, and terms snapshot.</Text>
          <View style={{ marginTop: 12, gap: 10 }}>
            {form.revisions.map((rev) => (
              <View key={rev.id} style={{ borderWidth: 1, borderColor: colors.border, borderRadius: 8, padding: 12, backgroundColor: "#f8fafc" }}>
                <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                    <Badge variant="warning">Rev {rev.revision_number}</Badge>
                    <Text style={{ fontWeight: "700", color: colors.text }}>{rev.quotation_number}</Text>
                  </View>
                  <Text style={{ fontSize: 13, color: colors.muted }}>
                    {String(rev.revised_at || "").slice(0, 16).replace("T", " ")}
                  </Text>
                </View>
                <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                  <Text style={{ fontSize: 13, color: colors.text }}>
                    Revised by: <Text style={{ fontWeight: "600" }}>{rev.revised_by_name || "Admin"}</Text> | Total: <Text style={{ fontWeight: "600" }}>PKR {Number(rev.grand_total || 0).toLocaleString("en-PK", { minimumFractionDigits: 2 })}</Text>
                  </Text>
                  <Button
                    icon={FileText}
                    variant="secondary"
                    style={{ height: 32, paddingHorizontal: 10 }}
                    onPress={() => window.open(api.quotationRevisionPdfUrl(id, rev.id), "_blank")}
                  >
                    View Revision PDF
                  </Button>
                </View>
                {rev.revision_notes ? (
                  <Text style={{ fontSize: 12, color: colors.muted, marginTop: 4, fontStyle: "italic" }}>
                    Notes: {rev.revision_notes}
                  </Text>
                ) : null}
              </View>
            ))}
          </View>
        </Card>
      ) : null}
    </View>
  );
}

function canEditStatus(status) {
  return ["draft", "revision_required", "rejected"].includes(String(status || "draft").toLowerCase());
}

function canSubmitStatus(status) {
  return canEditStatus(status);
}

function statusLabel(status) {
  const value = String(status || "draft").replace("_", " ");
  return value.charAt(0).toUpperCase() + value.slice(1);
}

const profitPanelStyle = {
  borderWidth: 1,
  borderColor: colors.primaryBorder,
  backgroundColor: colors.primaryLight,
  borderRadius: 12,
  padding: 16,
  gap: 12,
  width: "100%",
  marginTop: 12
};

const profitMetricStyle = {
  flex: 1,
  minWidth: 180,
  backgroundColor: "#fff",
  borderWidth: 1,
  borderColor: colors.border,
  borderRadius: 12,
  padding: 16,
  gap: 4
};

const profitValueStyle = {
  color: colors.text,
  fontSize: 20,
  fontWeight: "700"
};
